import { useState } from "react";

import { Input } from "@/common/ui/input";
import { Label } from "@/common/ui/label";

import { MIN_SEARCH_LENGTH, useWorkspaceSearch } from "./use-workspace-search";
import { WORKSPACE_SEARCH_LIMIT, type WorkspaceMatch } from "./workspace-search-api";

export interface WorkspaceSearchProps {
  readonly onSelect: (workspace: WorkspaceMatch) => void;
}

/**
 * Find the workspace to work on by NAME.
 *
 * The operator never types a scope id: they type a name, pick a row, and the id
 * travels from the row into the selection. That is the whole reason the lookup
 * route exists, and it is what keeps a mistyped character from pointing this
 * page at another customer.
 */
export function WorkspaceSearch({ onSelect }: WorkspaceSearchProps): React.ReactElement {
  const [term, setTerm] = useState("");
  /* The term a selection was made at, so the list closes once the operator has
     picked without discarding what they typed. Cleared by the next keystroke
     and by refocusing the box, so an operator who wants a different match from
     the same search has a way back to the list rather than a dead end. */
  const [pickedAt, setPickedAt] = useState<string | null>(null);
  const { matches, isSearching, error, tooShort } = useWorkspaceSearch(term);
  const picked = pickedAt !== null && pickedAt === term;
  const capped = matches !== null && matches.length >= WORKSPACE_SEARCH_LIMIT;

  return (
    <div className="space-y-2" data-testid="workspace-search">
      <Label htmlFor="workspace-search-input">Workspace</Label>
      <Input
        id="workspace-search-input"
        data-testid="workspace-search-input"
        type="search"
        value={term}
        onChange={(event) => {
          setTerm(event.target.value);
          setPickedAt(null);
        }}
        onFocus={() => setPickedAt(null)}
        placeholder="Search by name"
        autoComplete="off"
      />

      {tooShort && (
        <p className="text-sm text-muted-foreground" data-testid="workspace-search-too-short">
          Type at least {MIN_SEARCH_LENGTH} characters to search.
        </p>
      )}

      {error !== null && (
        <p className="text-sm text-destructive" data-testid="workspace-search-error">
          Workspace search failed: {error}
        </p>
      )}

      {isSearching && (
        <p className="text-sm text-muted-foreground" data-testid="workspace-search-loading">
          Searching…
        </p>
      )}

      {!picked && matches !== null && matches.length === 0 && (
        <p className="text-sm text-muted-foreground" data-testid="workspace-search-empty">
          No workspaces match that name.
        </p>
      )}

      {!picked && matches !== null && matches.length > 0 && (
        <ul className="divide-y rounded-md border" data-testid="workspace-search-results">
          {matches.map((workspace) => (
            <li key={workspace.id}>
              <button
                type="button"
                className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left hover:bg-muted"
                data-testid={`workspace-search-result-${workspace.id}`}
                onClick={() => {
                  setPickedAt(term);
                  onSelect(workspace);
                }}
              >
                <span className="font-medium">{workspace.name}</span>
                <span className="text-xs text-muted-foreground">
                  {workspace.kind} · {workspace.slug} · {workspace.status}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {!picked && capped && (
        <p className="text-sm text-muted-foreground" data-testid="workspace-search-capped">
          Showing the first {WORKSPACE_SEARCH_LIMIT} matches. Type more of the name to narrow them.
        </p>
      )}
    </div>
  );
}
